import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import {
  GraduationCap, BarChart3, Trophy, Briefcase, ArrowRight, CheckCircle,
  Building2, Award, Globe, Shield, Brain, Mic, Network, LineChart, Sparkles, Play, TrendingUp
} from 'lucide-react';
import Logo from '../components/landing/Logo';
import GrandFooter from '../components/landing/GrandFooter';

const API = process.env.REACT_APP_BACKEND_URL;

const FadeInSection = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const AnimatedCounter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end]);
  return <>{count.toLocaleString()}{suffix}</>;
};

const CollegesLandingPage = () => {
  const [partnerColleges, setPartnerColleges] = useState([]);
  const [totalPartners, setTotalPartners] = useState(0);
  const [institutionLogos, setInstitutionLogos] = useState([]);
  const [isNavScrolled, setIsNavScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setIsNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/public/partner-colleges`)
      .then((res) => {
        setPartnerColleges(res.data.colleges || []);
        setTotalPartners(res.data.total_partners || 0);
      }).catch(() => {});
    axios.get(`${API}/api/public/institution-logos`)
      .then((res) => setInstitutionLogos(res.data.logos || [])).catch(() => {});
  }, []);

  // Default placeholder institutions if API returns empty
  const displayLogos = institutionLogos.length > 0
    ? institutionLogos.map((l) => l.name || l.title || 'Institution')
    : ['IIT BOMBAY', 'IIT GANDHINAGAR', 'BITS PILANI', 'NIT TRICHY', 'IIIT HYDERABAD', 'VIT VELLORE', 'SRM UNIVERSITY', 'MANIPAL', 'JADAVPUR', 'DTU', 'NSUT', 'PES UNIVERSITY'];

  const pillars = [
    {
      icon: BarChart3,
      title: 'Placement Cell Dashboard',
      desc: 'Live readiness scores, skill gap heatmaps, and placement-ready cohort tracking — all in one command center.',
      cta: 'Explore Dashboard',
      testid: 'pillar-placement-cell',
    },
    {
      icon: Brain,
      title: 'AI Skill Assessments',
      desc: 'Proctored, AI-curated mock tests and interviews mapped to real hiring patterns of top recruiters.',
      cta: 'See Assessments',
      testid: 'pillar-ai-assessments',
    },
    {
      icon: Network,
      title: 'Direct Hiring Bridge',
      desc: 'Get your students discovered by 500+ companies through verified profiles and branded campus contests.',
      cta: 'Open Bridge',
      testid: 'pillar-hiring-bridge',
    },
  ];

  const outcomes = [
    {
      logo: 'IIT GANDHINAGAR',
      stat: '92%',
      statLabel: 'Placement readiness',
      title: 'Identified skill gaps 6 months ahead.',
      desc: 'AI-driven cohort heatmaps let placement cell focus prep where it actually moved the needle.',
      testid: 'outcome-iitgn',
    },
    {
      logo: 'BITS PILANI',
      stat: '3×',
      statLabel: 'Recruiter funnels',
      title: 'Tripled inbound recruiter visits in one term.',
      desc: 'Branded contests + verified leaderboards put their students in front of the right hiring teams faster.',
      testid: 'outcome-bits',
    },
    {
      logo: 'VIT VELLORE',
      stat: '100%',
      statLabel: 'Verified profiles',
      title: 'Zero impersonation in campus placements.',
      desc: 'Identity-verified, AI-proctored assessments meant every shortlist was real, every offer earned.',
      testid: 'outcome-vit',
    },
  ];

  const steps = [
    { num: '01', title: 'Onboard your campus', desc: 'Bring your students onto the platform in minutes — no infra setup required.' },
    { num: '02', title: 'Run skill cohorts', desc: 'AI-curated mock tests, mock interviews, and contests aligned to top recruiters.' },
    { num: '03', title: 'Track placement readiness', desc: 'Live dashboards show every student\'s placement-ready score and skill gaps.' },
    { num: '04', title: 'Connect to recruiters', desc: 'Verified leaderboards open the door to 500+ hiring teams — direct, no middlemen.' },
  ];

  return (
    <div className="min-h-screen bg-white text-[#171717]" data-testid="colleges-landing">
      {/* Sticky glass-blur Nav */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${isNavScrolled ? 'bg-white/85 backdrop-blur-md border-b border-[#E5E5E5] shadow-sm py-3' : 'bg-transparent py-5'}`}
        data-testid="colleges-nav"
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 flex items-center justify-between">
          <Logo subtitle="for Colleges" size="md" />
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="colleges-nav-home">Home</Link>
            <Link to="/codearena" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="colleges-nav-arena">EvalentX Arena</Link>
            <Link to="/company" className="text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="colleges-nav-companies">For Companies</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/college/auth" className="hidden md:inline text-sm font-semibold text-[#525252] hover:text-[#0A0A0A] transition-colors" data-testid="colleges-nav-signin">Sign In</Link>
            <Link to="/college/auth" className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-sm font-bold hover:bg-[#171717] hover:-translate-y-0.5 transition-all" data-testid="colleges-nav-cta">
              Partner With Us
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-28 md:pt-40 md:pb-36 min-h-[calc(100vh-3.5rem)] flex items-center overflow-hidden" data-testid="colleges-hero">
        <div className="absolute top-1/4 -right-1/4 text-[18vw] font-black text-[#B91C1C] opacity-[0.025] -rotate-12 pointer-events-none select-none tracking-tighter leading-none">
          CAMPUS
        </div>

        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative z-10 w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <FadeInSection className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 mb-8">
                <span className="text-[11px] font-black uppercase tracking-widest text-[#B91C1C]">FOR UNIVERSITIES &amp; COLLEGES</span>
              </div>

              <h1 className="text-6xl md:text-7xl lg:text-[7rem] font-extrabold text-[#0A0A0A] tracking-tighter leading-[0.95] mb-10" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Empower your <br />campus. <br />
                <span className="relative inline-block">
                  Place every student.
                  <motion.div
                    initial={{ width: 0 }} animate={{ width: '100%' }}
                    transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute -bottom-1 left-0 h-3 bg-[#B91C1C]/25 -z-10"
                  />
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-[#525252] mb-12 leading-relaxed max-w-xl font-medium">
                Transform your placement cell with AI-powered analytics, proctored assessments, and direct connections to 500+ recruiters across India.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/college/auth" className="px-9 py-5 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.25)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.35)] hover:-translate-y-1 transition-all flex items-center gap-2 group" data-testid="colleges-hero-cta-primary">
                  Partner With EvalentX <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="px-9 py-5 bg-white text-[#0A0A0A] border-[1.5px] border-[#E5E5E5] hover:border-[#0A0A0A] rounded-full font-bold transition-all flex items-center gap-2 group" data-testid="colleges-hero-cta-secondary">
                  <Play size={18} className="fill-current text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors" /> Request Demo
                </button>
              </div>

              <div className="flex items-center gap-6 mt-10 text-sm text-[#525252] font-medium">
                <span className="inline-flex items-center gap-2"><CheckCircle size={16} className="text-[#B91C1C]" /> Free to get started</span>
                <span className="inline-flex items-center gap-2"><CheckCircle size={16} className="text-[#B91C1C]" /> {totalPartners > 0 ? `${totalPartners}+` : '500+'} colleges onboarded</span>
              </div>
            </FadeInSection>

            {/* Placement Dashboard Mockup */}
            <FadeInSection delay={0.2} className="lg:col-span-5 relative">
              <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#F5F5F5]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A0A0A] rounded-xl grid place-items-center">
                      <GraduationCap className="text-white" size={18} />
                    </div>
                    <div>
                      <div className="text-[#0A0A0A] font-bold text-sm">Campus Placement</div>
                      <div className="text-[#A3A3A3] text-xs font-bold uppercase tracking-widest">2026 Cohort</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B91C1C] bg-[#FEF2F2] border border-[#B91C1C]/15 rounded-full px-3 py-1">Live</span>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { label: 'Students', value: '847', accent: false },
                    { label: 'Placement Ready', value: '92%', accent: true },
                    { label: 'Offers', value: '156', accent: false },
                  ].map((c, i) => (
                    <div key={i} className={`rounded-xl border p-3 ${c.accent ? 'bg-[#FEF2F2] border-[#B91C1C]/20' : 'bg-[#FAFAF9] border-[#E5E5E5]'}`}>
                      <div className={`text-xl font-extrabold tracking-tight ${c.accent ? 'text-[#B91C1C]' : 'text-[#0A0A0A]'}`} style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>{c.value}</div>
                      <div className="text-[10px] font-bold text-[#A3A3A3] uppercase tracking-widest mt-1">{c.label}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2.5">
                  {[
                    { label: 'AI / ML', value: 88, w: '88%' },
                    { label: 'Web Development', value: 76, w: '76%' },
                    { label: 'Data Structures', value: 91, w: '91%' },
                    { label: 'System Design', value: 64, w: '64%' },
                  ].map((skill, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-[11px] font-semibold mb-1">
                        <span className="text-[#525252]">{skill.label}</span>
                        <span className="text-[#0A0A0A]">{skill.value}%</span>
                      </div>
                      <div className="h-1.5 bg-[#F5F5F5] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${skill.value >= 85 ? 'bg-[#B91C1C]' : 'bg-[#0A0A0A]'}`} style={{ width: skill.w }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white border-y border-[#E5E5E5]" data-testid="colleges-stats">
        <div className="max-w-[1280px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#E5E5E5]">
          {[
            { value: 50000, suffix: '+', label: 'Students Trained', accent: false },
            { value: totalPartners > 0 ? totalPartners : 2000, suffix: '+', label: 'Partner Colleges', accent: false },
            { value: 500, suffix: '+', label: 'Hiring Companies', accent: false },
            { value: 95, suffix: '%', label: 'Placement Success', accent: true },
          ].map((s, i) => (
            <div key={i} className="py-16 md:py-20 px-6 md:px-10 group hover:bg-[#FAFAF9] transition-colors">
              <span
                className={`block text-5xl md:text-6xl lg:text-[5.5rem] font-extrabold tracking-tighter mb-3 leading-none ${s.accent ? 'text-[#B91C1C]' : 'text-[#0A0A0A] group-hover:text-[#B91C1C]'} transition-colors`}
                style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
              >
                <AnimatedCounter end={s.value} suffix={s.suffix} />
              </span>
              <span className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em]">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Trusted by */}
      <section className="bg-white py-16 border-b border-[#E5E5E5]" data-testid="colleges-trusted-by">
        <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] text-center mb-10">Trusted by 500+ leading institutions</p>
        <Marquee gradient gradientColor="rgb(255,255,255)" speed={40}>
          {displayLogos.map((name, i) => (
            <div key={`${name}-${i}`} className="flex items-center gap-3 mx-10 text-[#A3A3A3] hover:text-[#0A0A0A] transition-colors">
              <GraduationCap size={16} />
              <span className="text-sm font-extrabold tracking-[0.15em] uppercase">{name}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* Three Pillars */}
      <section className="py-24 md:py-36 bg-white border-b border-[#E5E5E5]" data-testid="colleges-pillars">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4">Built for placement cells</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Three pillars. <br /><span className="text-[#B91C1C]">One unfair advantage.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                A modern placement cell needs more than spreadsheets — it needs live skill data, proctored assessments, and direct access to recruiters. EvalentX gives you all three in one place.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((p) => (
              <div
                key={p.title}
                className="group bg-white border border-[#E5E5E5] rounded-2xl p-8 md:p-10 hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.18)] hover:-translate-y-1 transition-all duration-500"
                data-testid={p.testid}
              >
                <div className="w-14 h-14 rounded-xl bg-[#FEF2F2] border border-[#B91C1C]/20 grid place-items-center mb-6">
                  <p.icon size={22} className="text-[#B91C1C]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#0A0A0A] mb-4 leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  {p.title}
                </h3>
                <p className="text-[#525252] text-[15px] leading-relaxed mb-8 font-medium">{p.desc}</p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors">
                  {p.cta} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-36 bg-[#FAFAF9] border-b border-[#E5E5E5]" data-testid="colleges-how">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4">How EvalentX works</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Four steps from <br /><span className="text-[#B91C1C]">campus to career.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                A single, opinionated playbook for placement teams — designed by people who actually shipped placements at scale.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {steps.map((s) => (
              <div key={s.num} className="bg-white border border-[#E5E5E5] rounded-2xl p-7 hover:border-[#B91C1C] hover:-translate-y-1 transition-all duration-500" data-testid={`how-step-${s.num}`}>
                <span className="block text-[5rem] md:text-[5.5rem] font-extrabold tracking-tighter leading-none text-[#FAFAF9] mb-2" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                  {s.num}
                </span>
                <h3 className="text-xl font-extrabold tracking-tight text-[#0A0A0A] mb-3 leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed font-medium">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-24 md:py-36 bg-white border-b border-[#E5E5E5]" data-testid="colleges-outcomes">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.25em] mb-4">Campus Outcomes</p>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                Real campuses. <br /><span className="text-[#B91C1C]">Real placements.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed">
                Institutions across India are using EvalentX to lift placement readiness, multiply recruiter funnels, and stop losing offers to last-minute skill gaps.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {outcomes.map((o) => (
              <div key={o.logo} className="group bg-white border border-[#E5E5E5] rounded-2xl p-8 md:p-10 hover:border-[#B91C1C] hover:shadow-[0_24px_48px_-12px_rgba(185,28,28,0.18)] hover:-translate-y-1 transition-all duration-500" data-testid={o.testid}>
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-[#F5F5F5]">
                  <span className="text-sm font-extrabold tracking-[0.25em] text-[#171717]" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>{o.logo}</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Case Study</span>
                </div>
                <div className="mb-8">
                  <div className="text-5xl md:text-6xl font-extrabold tracking-tighter text-[#B91C1C] leading-none mb-2" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>{o.stat}</div>
                  <p className="text-[11px] font-bold text-[#A3A3A3] uppercase tracking-[0.2em]">{o.statLabel}</p>
                </div>
                <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-[#0A0A0A] mb-4 leading-tight" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>{o.title}</h3>
                <p className="text-[#525252] text-[15px] leading-relaxed mb-8 font-medium">{o.desc}</p>
                <span className="inline-flex items-center gap-2 text-sm font-bold text-[#0A0A0A] group-hover:text-[#B91C1C] transition-colors">
                  Read story <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 md:py-40 bg-[#FAFAF9]" data-testid="colleges-cta-bottom">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[11px] font-bold text-[#B91C1C] uppercase tracking-[0.3em] mb-6">For Placement Cells</p>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-[#0A0A0A] mb-10 tracking-tighter" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            Bring EvalentX <br /><span className="text-[#B91C1C]">to your campus.</span>
          </h2>
          <p className="text-lg md:text-xl text-[#525252] font-medium leading-relaxed mb-12 max-w-2xl mx-auto">
            Join {totalPartners > 0 ? `${totalPartners}+` : '500+'} institutions already using EvalentX to prepare students and connect them to recruiters.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/college/auth" className="px-10 py-5 bg-[#B91C1C] text-white rounded-full font-bold shadow-[0_8px_24px_rgba(185,28,28,0.25)] hover:shadow-[0_12px_32px_rgba(185,28,28,0.35)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 text-lg group">
              Partner With Us <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <button className="px-10 py-5 bg-white text-[#0A0A0A] border-[1.5px] border-[#E5E5E5] hover:border-[#0A0A0A] rounded-full font-bold transition-all flex items-center justify-center gap-2 text-lg">
              <Play size={18} className="fill-current text-[#0A0A0A]" /> Watch Demo
            </button>
          </div>
        </div>
      </section>

      <GrandFooter />
    </div>
  );
};

export default CollegesLandingPage;
